import React, { useEffect, useState } from "react";
import commonStyles from "../../styles/Common.module.css";
import {
    Typography,
    Grid,
    Card,
    CardContent,
    TextField,
    Button,
    Divider,
    Tabs,
    Tab,
    FormControlLabel,
    Switch,
    Chip, 
    Stack
} from "@mui/material";
import { useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { styled } from "@mui/material/styles";

const getSubscriptionLabel = (key) => {
    switch (key) {
        case "news":
            return "News";
        case "full-access":
            return "Full Access";
        case "basket":
            return "SD";
        case "trial":
            return "Trial";
        case "life":
            return "Life";
        case "monthly":
            return "Monthly";
        case "quarterly":
            return "Quarterly";
        default:
            return key;
    }
};

const HoverCard = styled(Card)(({ theme }) => ({
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
    '&:hover': {
        transform: "scale(1.02)",
        boxShadow: theme.shadows[6],
        backgroundColor: theme.palette.action.hover
    }
}));

export const MentorBatchDetails = () => {
    const { id } = useParams();
    const [batch, setBatch] = useState(null);
    const [formState, setFormState] = useState(null);
    const [isModified, setIsModified] = useState(false);
    const [tab, setTab] = useState(0);
    const [sessions, setSessions] = useState([]);
    const [sessionEdits, setSessionEdits] = useState({});
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);

    useEffect(() => {
        fetchBatch();
        fetchSessions();
    }, []);

    const fetchBatch = async () => {
        const res = await axios.get(`https://api.sovrenn.com/mentor-batches/${id}`, {
            headers: {
                "Authorization": "Bearer " + localStorage.getItem("sov_user"),
            },
        });
        const batchData = res.data.data[0];
        setBatch(batchData);
        setFormState({
            total_seats: batchData.total_seats || "",
            seats_left: batchData.seats_left,
            batch_start_date: moment(new Date(batchData.batch_start_date)).format("YYYY-MM-DD"),
            enrollment_start_date: moment(new Date(batchData.enrollment_start_date)).format("YYYY-MM-DD"),
            enrollment_last_date: moment(new Date(batchData.enrollment_last_date)).format("YYYY-MM-DD"),
            whatsapp_group_link: batchData.whatsapp_group_link || ""
        });
    };


    const fetchSessions = async () => {
        const res = await axios.get(`https://api.sovrenn.com/mentor-sessions/${id}`, {
            headers: {
                "Authorization": "Bearer " + localStorage.getItem("sov_user"),
            },
        });

        setSessions(res.data.data);
    };

    const handleUpdate = async () => {
        await axios.patch(`https://api.sovrenn.com/mentor-batches/${id}`, formState, {
            headers: {
                "Authorization": "Bearer " + localStorage.getItem("sov_user"),
            },
        });
        setIsModified(false);
        fetchBatch();
    };

    const handleInputChange = (key, value) => {
        setFormState((prev) => {
            const updated = { ...prev, [key]: value };
            setIsModified(
                updated.total_seats !== batch.total_seats ||
                updated.seats_left !== batch.seats_left ||
                updated.batch_start_date !== moment(new Date(batch.batch_start_date)).format("YYYY-MM-DD") ||
                updated.enrollment_start_date !== moment(new Date(batch.enrollment_start_date)).format("YYYY-MM-DD") ||
                updated.enrollment_last_date !== moment(new Date(batch.enrollment_last_date)).format("YYYY-MM-DD") ||
                updated.whatsapp_group_link !== (batch.whatsapp_group_link || "")
            );
            return updated;
        });
    };


    const handleSessionEdit = (sessionId, key, value) => {
        setSessionEdits((prev) => ({
            ...prev,
            [sessionId]: {
                ...prev[sessionId],
                [key]: value
            }
        }));
    };

    const saveSession = async (sessionId) => {
        await axios.patch(`https://api.sovrenn.com/mentor-sessions/${sessionId}`, sessionEdits[sessionId], {
            headers: {
                "Authorization": "Bearer " + localStorage.getItem("sov_user"),
            },
        });
        fetchSessions();
    };

    const handleUpload = async (sessionId, session) => {
        if (!file) return alert("Please select a file");

        try {
            setUploading(true);
            const fileType = file.name.split(".").pop();
            const fileName = session.title.replaceAll(" ", "") + moment(session.date).format("DDMMYYYY")
            const { data } = await axios.get(`https://api.sovrenn.com/mentor-sessions/generate/upload-url?fileType=${fileType}&fileName=${fileName}`, {
                headers: {
                    "Authorization": "Bearer " + localStorage.getItem("sov_user"),
                },
            });

            const duration = prompt("Enter video duration in seconds:");
            if (!duration) return alert("Video duration is required");

            await axios.put(data.uploadUrl, file, {
                headers: { "Content-Type": file.type }
            });

            await axios.patch(`https://api.sovrenn.com/mentor-sessions/${sessionId}`, {
                is_recorded: true,
                recording: {
                    video_url: data.fileUrl,
                    thumb_image_url: session.thumb_image_url,
                    video_duration: Number(duration)
                }
            }, {
                headers: {
                    "Authorization": "Bearer " + localStorage.getItem("sov_user"),
                },
            });

            alert("Upload successful");
            fetchSessions();
        } catch (err) {
            console.error("Upload error:", err);
            setFile(null);
            alert("Upload failed");
        } finally {
            setFile(null);
            setUploading(false);
        }
    };

    if (!batch || !formState) return (
        <div className={commonStyles.MainContainer}>

            <div className={commonStyles.headerDiv}>

                <Typography variant="h5">Loading...</Typography>

            </div>
        </div>
    );

    return (
        <div className={commonStyles.MainContainer}>

            <div className={commonStyles.headerDiv}>
                <Typography variant="h4" gutterBottom>
                    Batch: {batch.batch_name}
                </Typography>

            </div>

            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Total Seats"
                        value={formState.total_seats}
                        onChange={(e) => handleInputChange("total_seats", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Seats Left"
                        value={formState.seats_left}
                        onChange={(e) => handleInputChange("seats_left", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Enrollment Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={formState.enrollment_start_date}
                        onChange={(e) => handleInputChange("enrollment_start_date", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Enrollment Last Date"
                        InputLabelProps={{ shrink: true }}
                        value={formState.enrollment_last_date}
                        onChange={(e) => handleInputChange("enrollment_last_date", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Batch Start Date"
                        InputLabelProps={{ shrink: true }}
                        value={formState.batch_start_date}
                        onChange={(e) => handleInputChange("batch_start_date", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="WhatsApp Group Link"
                        value={formState.whatsapp_group_link}
                        onChange={(e) => handleInputChange("whatsapp_group_link", e.target.value)}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={!isModified}
                    >
                        Update
                    </Button>
                </Grid>
            </Grid>

            <Tabs value={tab} onChange={(e, val) => setTab(val)}>
                <Tab label={`Enrolled Users (${batch.enrolled_users?.length})`} />
                <Tab label={`Sessions (${sessions.length})`} />
            </Tabs>

            {tab === 0 && (
                <Grid container spacing={2} style={{ marginTop: 10 }}>
                    {batch.enrolled_users?.map((user, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card>
                                <HoverCard onClick={() => window.open(`/users/${user._id}`, "_blank")}>
                                    <CardContent>
                                        <Typography variant="h5">
                                            {user.first_name.toUpperCase()} {user.last_name.toUpperCase()}
                                        </Typography>
                                        <Typography>Email: {user.email}</Typography>
                                        <Typography>Phone: {user.phone_number}</Typography>
                                        <Typography>Amount Paid: ₹{user.amount_paid}</Typography>
                                        <Typography>Bought From: {user.bought_from}</Typography>
                                        <Typography>
                                            Certificate Issued: {user.is_certificate_issued ? "Yes" : "No"}
                                        </Typography>
                                        <Typography>Joined: {moment(user.createdAt).format("Do MMM YY")}</Typography>

                                        <Stack direction="row" spacing={1} marginTop={1} flexWrap="wrap">
                                            {user.subscriptions?.map((sub, idx) => (
                                                <Chip
                                                    key={idx}
                                                    label={getSubscriptionLabel(sub)}
                                                    color="primary"
                                                    size="small"
                                                    style={{ marginBottom: 4 }}
                                                />
                                            ))}
                                        </Stack>
                                    </CardContent>
                                </HoverCard>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )
            }

            {
                tab === 1 && (
                    <Grid container spacing={2} style={{ marginTop: 10 }}>
                        {sessions.map((session) => (
                            <Grid item xs={12} md={6} key={session._id}>
                                <Card style={session.is_live ? { backgroundColor: "#e8f5e9" } : {}}>
                                    <CardContent>
                                        <TextField
                                            fullWidth
                                            label="Title"
                                            value={sessionEdits[session._id]?.title ?? session.title}
                                            onChange={(e) => handleSessionEdit(session._id, "title", e.target.value)}
                                            style={{ marginBottom: 8 }}
                                        />
                                        <TextField
                                            fullWidth
                                            label="Zoom Link"
                                            value={sessionEdits[session._id]?.zoom_link ?? session.zoom_link}
                                            onChange={(e) => handleSessionEdit(session._id, "zoom_link", e.target.value)}
                                            style={{ marginBottom: 8 }}
                                        />
                                        <TextField
                                            fullWidth
                                            type="date"
                                            label="Date"
                                            value={sessionEdits[session._id]?.date ?? moment(session.date).format("YYYY-MM-DD")}
                                            onChange={(e) => handleSessionEdit(session._id, "date", e.target.value)}
                                            style={{ marginBottom: 8 }}
                                            InputLabelProps={{ shrink: true }}
                                        />
                                        <TextField
                                            fullWidth
                                            type="time"
                                            label="Start Time"
                                            value={sessionEdits[session._id]?.start_time ?? session.start_time}
                                            onChange={(e) => handleSessionEdit(session._id, "start_time", e.target.value)}
                                            style={{ marginBottom: 8 }}
                                        />
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={sessionEdits[session._id]?.is_live ?? session.is_live}
                                                    onChange={(e) => handleSessionEdit(session._id, "is_live", e.target.checked)}
                                                />
                                            }
                                            label="Is Live"
                                        />
                                        <Typography>Instructor: {session.instructor?.first_name} {session.instructor?.last_name}</Typography>
                                        <Typography>Recorded: {session.is_recorded ? "Yes" : "No"}</Typography>

                                        <input
                                            type="file"
                                            accept="video/*"
                                            onChange={(e) => setFile(e.target.files[0])}
                                            style={{ marginTop: 10 }}
                                        />

                                        <Grid container spacing={2} style={{ marginTop: 8 }}>
                                            <Grid item xs={6}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={() => handleUpload(session._id, session)}
                                                    disabled={uploading}
                                                >
                                                    {uploading ? "Uploading..." : "Upload Recording"}
                                                </Button>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Button
                                                    fullWidth
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={() => saveSession(session._id)}
                                                >
                                                    Save Changes
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )
            }
        </div >
    );
}